import Http from '../../../core/Http';

/**
 * 테마 설정 전역 객체 (window.ThemeConfig)
 *
 * DB와 연동하여 테마를 관리합니다.
 * 1. 초기 로드: DB에서 최신 설정을 가져옵니다.
 * 2. 실시간 편집: 변경 사항은 즉시 localStorage에 저장됩니다.
 * 3. 최종 저장: 사용자가 '저장' 시, localStorage의 내용을 DB로 전송하고 localStorage를 비웁니다.
 */
const defaultConfig = {
  workspaceId: '1',
  navLayout: 'top',
  submenuType: 'mega', // 상단 배치일 때: 'mega' | 'dropdown'
  menuScrollType: 'wrap',
  fixedMenuCount: 6, // 'fixedWithMore'일 때 상단에 고정 표시할 메뉴 항목 수
  menuAlign: 'left',
  mobileMenuStyle: 'modern',
  themeColor: '#e97730',
  /** 보조 테마색(티일 등). 원격 제어기에서 변경 가능, 기본 #146e7a */
  themeColorSecondary: '#146e7a',
  headerTopBackgroundColor: '#f8f8f8',
  headerTopTextColor: '#000000',
  sidebarColumns: 3,
  gridGap: 1.25,
  containerWidths: { xl: 1400, lg: 992, md: 768, sm: 576 },
  areaContainerWidths: {
    'header-top': { xl: null, lg: null, md: null, sm: null },
    'header-bottom': { xl: null, lg: null, md: null, sm: null },
    'banner': { xl: null, lg: null, md: null, sm: null },
    'content': { xl: null, lg: null, md: null, sm: null },
    'footer': { xl: null, lg: null, md: null, sm: null },
    'related-sites': { xl: null, lg: null, md: null, sm: null }
  },
  fontStack: '"Pretendard", "Noto Sans KR", Helvetica, Arial, serif',
  primaryFont: 'Pretendard',
  fallbackFonts: '"Noto Sans KR", Helvetica, Arial, serif',
  rightSidebarType: 'simple',
  rightSidebarWidth: 420,
  footerTheme: 'simple',
  copyrightAlignment: 'center',
  copyrightBackgroundColor: '#343a40',
  copyrightTextColor: '#ffffff',
  copyrightHeight: 10,
  bannerType: 'center-image-dynamic',
  sidebarMenuType: 'accordion',
  headerTopHeight: 10,
  headerBottomHeight: 15,
  headerTopVisible: true,
  megaMenuColumns: 1,
  megaMenuSectionMaxColumns: 4,
  headerBottomBackgroundColor: '#ffffff',
  headerBottomTextColor: '#000000',
  buttonTheme: 'btn-theme01',
  aosEnabled: true,
  relatedSitesEnabled: true,
  relatedSitesType: 'image-thumbnail',
  relatedSitesShowTitle: true,
  relatedSitesShowImage: true,
  relatedSitesShowUrl: false,
  relatedSitesListExpanded: false,
  scrollToTopButtonType: 'default', // 'default', 'with-text', 'modern'
  // 헤더 로고 설정 (HeaderAreaManager.jsx getDefaultHeaderLogo() 구조와 동일, 상단 로고 관리 페이지에서 편집)
  /** DB workplace_theme_config.theme_remote_control_visible — 메뉴 권한과 함께 원격 제어기 노출에 사용 */
  themeRemoteControlVisible: false,
  headerLogo: {
    type: 'image', // 'image' | 'tag'
    image: '/assets/images/logo/logo.png', // 이미지 타입일 때 사용
    // 태그 타입일 때 사용 (다국어: title/subtitle은 { kor, eng })
    tag: {
      logo: {
        size: 49,
        backgroundColor: 'rgb(147, 51, 234)',
        color: '#ffffff',
        fontSize: '1.5rem',
        letter: 'G'
      },
      title: { kor: 'GLOW Platform', eng: 'GLOW Platform' },
      titleColor: '#6b21a8',
      titleStyle: { fontSize: '20px', fontWeight: 700, lineHeight: 1.2, letterSpacing: '0', textTransform: 'none' },
      subtitle: { kor: '지산학 R&D 플랫폼', eng: 'Industry-Academia R&D Platform' },
      subtitleColor: '#6b7280',
      subtitleStyle: { fontSize: '14px', fontWeight: 400, lineHeight: 1.5, letterSpacing: '0', textTransform: 'none' }
    }
  }
};

const STORAGE_KEY = 'themeConfig_local'; // LocalStorage 키를 변경하여 이전 버전과 충돌 방지
const STORAGE_KEY_ALLIANCE = 'themeConfig_alliance_local'; // Alliance 경로용 localStorage 키
/** DB 로드 성공 시 저장 — API 타임아웃·500 시 기본 테마로 깜빡이지 않도록 */
const SNAPSHOT_PREFIX = 'themeConfig_snapshot_ws_';
const SNAPSHOT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const THEME_API_TIMEOUT_MS = 20000;
const THEME_API_RETRY_COUNT = 2;

/**
 * 경로와 workspaceId 매핑 설정
 * 새로운 workspace를 추가할 때 여기에 경로와 workspaceId를 추가하면 됩니다.
 *
 * @type {Object<string, number>}
 * - key: 경로 prefix (예: '/alliance')
 * - value: workspaceId (예: 2)
 */
const WORKSPACE_PATH_MAP = {
  '/alliance': 2,
  // 기본 경로는 workspaceId: 1 (매핑에 없으면 기본값 사용)
  // 새로운 workspace 추가 예시:
  // '/another-workspace': 3,
  // '/custom-path': 4,
};

/**
 * 경로에서 workspaceId를 찾는 헬퍼 함수
 * @param {string} pathname - 현재 경로 (예: '/alliance/dashboard')
 * @returns {number} workspaceId (기본값: 1)
 */
function getWorkspaceIdFromPath(pathname) {
  if (!pathname || pathname === '/') {
    return 1; // 기본 workspaceId
  }

  // 경로 prefix를 길이 순으로 정렬하여 가장 긴 매칭 경로 찾기
  const sortedPaths = Object.keys(WORKSPACE_PATH_MAP).sort((a, b) => b.length - a.length);

  for (const pathPrefix of sortedPaths) {
    if (pathname.startsWith(pathPrefix)) {
      return WORKSPACE_PATH_MAP[pathPrefix];
    }
  }

  return 1; // 기본값
}

/**
 * 경로에서 storage key를 생성하는 헬퍼 함수
 * @param {string} pathname - 현재 경로
 * @returns {string} storage key
 */
function getStorageKeyFromPath(pathname) {
  const workspaceId = getWorkspaceIdFromPath(pathname);
  if (workspaceId === 2) {
    return STORAGE_KEY_ALLIANCE; // 하위 호환성을 위해 유지
  }
  // 새로운 workspace가 추가되면 동적으로 생성할 수도 있음
  return STORAGE_KEY;
}

function readThemeSnapshot(workspaceId) {
  try {
    const raw = sessionStorage.getItem(`${SNAPSHOT_PREFIX}${workspaceId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.config || !parsed?.at) return null;
    if (Date.now() - Number(parsed.at) > SNAPSHOT_MAX_AGE_MS) return null;
    return parsed.config;
  } catch {
    return null;
  }
}

function writeThemeSnapshot(workspaceId, config) {
  try {
    sessionStorage.setItem(
      `${SNAPSHOT_PREFIX}${workspaceId}`,
      JSON.stringify({ config, at: Date.now() })
    );
  } catch (e) {
    console.warn('[ThemeConfig] sessionStorage 스냅샷 저장 실패:', e);
  }
}

function readLocalThemeDraft(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** DB 조회 실패 시: 스냅샷 → localStorage 편집본 → 현재 화면 테마 → 기본값 */
function resolveThemeOnFetchFailure(workspaceId, storageKey) {
  const snapshot = readThemeSnapshot(workspaceId);
  if (snapshot) {
    console.warn(`[ThemeConfig] DB 실패 — 최근 저장 스냅샷 사용 (workspaceId: ${workspaceId})`);
    return deepMerge({}, defaultConfig, snapshot);
  }
  const localDraft = readLocalThemeDraft(storageKey);
  if (localDraft) {
    console.warn(`[ThemeConfig] DB 실패 — localStorage 테마 사용 (workspaceId: ${workspaceId})`);
    return deepMerge({}, defaultConfig, localDraft);
  }
  if (typeof window !== 'undefined' && window.__themeConfig) {
    console.warn(`[ThemeConfig] DB 실패 — 현재 적용 테마 유지 (workspaceId: ${workspaceId})`);
    return deepMerge({}, defaultConfig, window.__themeConfig);
  }
  console.warn(`[ThemeConfig] DB 실패 — 기본 테마 사용 (workspaceId: ${workspaceId})`);
  return deepMerge({}, defaultConfig);
}

async function fetchThemeFromApi(workspaceId) {
  const url = `/api/system/theme-config/workspace/${workspaceId}/latest`;
  let lastError;
  for (let attempt = 1; attempt <= THEME_API_RETRY_COUNT; attempt += 1) {
    try {
      return await Http.get(url, { timeout: THEME_API_TIMEOUT_MS });
    } catch (e) {
      lastError = e;
      if (attempt < THEME_API_RETRY_COUNT) {
        await new Promise((resolve) => setTimeout(resolve, 400));
      }
    }
  }
  throw lastError;
}

// 유틸리티 함수
const isObject = (item) => item && typeof item === 'object' && !Array.isArray(item);

const deepMerge = (target, ...sources) => {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        deepMerge(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }
  return deepMerge(target, ...sources);
};

export const ThemeConfig = {
  config: deepMerge({}, defaultConfig),
  defaultConfig: defaultConfig,
  STORAGE_KEY: STORAGE_KEY,
  _initializationPromise: null, // 초기화 Promise 저장
  _loadingPath: null,
  _loadingPromise: null,
  _lastLoadedPath: null,
  _lastLoadedWorkspaceId: null,

  /** 동일 workspace 캐시 무효화 후 latest API를 다시 타게 함 (테마 관리에서 플래그 변경 시) */
  invalidateWorkspaceThemeCache(workspaceId) {
    const id = Number(workspaceId);
    if (!Number.isFinite(id)) return;
    if (this._lastLoadedWorkspaceId === id) {
      this._lastLoadedWorkspaceId = null;
      this._lastLoadedPath = null;
    }
  },

  /**
   * 경로에 따라 적절한 테마를 로드합니다.
   * @param {string} pathname - 현재 경로
   * @returns {Promise<object>} 테마 설정 객체
   */
  loadThemeForPath: async function (pathname) {
    const targetPath = pathname || '/';
    const workspaceId = getWorkspaceIdFromPath(targetPath);
    const storageKey = getStorageKeyFromPath(targetPath);

    // 동일 workspace면 캐시된 설정 재사용 (경로만 다른 경우 재조회 방지)
    if (this._lastLoadedWorkspaceId === workspaceId && typeof window !== 'undefined' && window.__themeConfig) {
      this.STORAGE_KEY = storageKey;
      this._lastLoadedPath = targetPath;
      return Promise.resolve(window.__themeConfig);
    }

    if (this._loadingPath === targetPath && this._loadingPromise) {
      return this._loadingPromise;
    }
    if (this._lastLoadedPath === targetPath && typeof window !== 'undefined' && window.__themeConfig) {
      return Promise.resolve(window.__themeConfig);
    }

    this._loadingPath = targetPath;
    this._loadingPromise = (async () => {
      let themeConfig = {};

      // DB에서 workspaceId에 해당하는 테마 로드
      try {
        const data = await fetchThemeFromApi(workspaceId);

        if (data && data.themeData) {
          // DB에 데이터가 있으면 적용
          let dbTheme = data.themeData;
          if (typeof dbTheme === 'string') {
            dbTheme = JSON.parse(dbTheme);
          }
          themeConfig = deepMerge({}, defaultConfig, dbTheme);
          themeConfig.themeRemoteControlVisible =
            data.themeRemoteControlVisible === true || data.themeRemoteControlVisible === 'true';
          if (window.isDevConsoleEnabled?.()) {
            console.log(`[ThemeConfig] DB 테마 로드 완료 (workspaceId: ${workspaceId})`);
          }
          writeThemeSnapshot(workspaceId, themeConfig);
        } else {
          themeConfig = resolveThemeOnFetchFailure(workspaceId, storageKey);
          if (data && (data.themeRemoteControlVisible === true || data.themeRemoteControlVisible === 'true')) {
            themeConfig.themeRemoteControlVisible = true;
          }
        }
      } catch (e) {
        console.warn(`[ThemeConfig] DB 테마 가져오기 실패 (workspaceId: ${workspaceId}):`, e);
        themeConfig = resolveThemeOnFetchFailure(workspaceId, storageKey);
      }

      // 기본 설정과 병합하여 최종 설정 결정
      this.config = deepMerge({}, defaultConfig, themeConfig);
      this.STORAGE_KEY = storageKey; // 현재 경로에 맞는 storage key 설정

      // 전역 객체 및 React Context에 최종 설정 반영
      if (typeof window !== 'undefined') {
        window.__themeConfig = this.config;
        if (window.__ThemeContextInstance && window.__ThemeContextInstance.setConfig) {
          window.__ThemeContextInstance.setConfig(this.config);
        }
      }

      return this.config;
    })();

    try {
      const result = await this._loadingPromise;
      this._lastLoadedPath = targetPath;
      this._lastLoadedWorkspaceId = workspaceId;
      return result;
    } finally {
      this._loadingPath = null;
      this._loadingPromise = null;
    }
  },

  /**
   * 테마 초기화: DB 또는 localStorage에서 설정을 로드합니다.
   * 페이지 로드 시 한 번만 호출되어야 합니다.
   */
  initialize: async function () {
    // 이미 초기화가 진행 중이면 기존 Promise 반환
    if (this._initializationPromise) {
      return this._initializationPromise;
    }

    // 초기화 Promise 생성
    this._initializationPromise = (async () => {
      // 현재 경로 확인
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

      // 경로에 따라 적절한 테마 로드
      return await this.loadThemeForPath(currentPath);
    })();

    return this._initializationPromise;
  },

  /**
   * 초기화 완료 대기 (이미 완료되었으면 즉시 반환)
   * @returns {Promise<object>} 테마 설정 객체
   */
  waitForInitialization: async function () {
    // 이미 window.__themeConfig가 설정되어 있으면 즉시 반환
    if (typeof window !== 'undefined' && window.__themeConfig) {
      return Promise.resolve(window.__themeConfig);
    }

    // 초기화가 진행 중이거나 아직 시작되지 않았으면 초기화 실행
    if (!this._initializationPromise) {
      return this.initialize();
    }

    // 초기화 진행 중이면 기존 Promise 반환
    return this._initializationPromise;
  },

  /**
   * 현재 설정을 localStorage에 저장 (실시간 편집용)
   * 현재 경로에 맞는 storage key 사용
   */
  saveToLocal: function () {
    try {
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
      const storageKey = getStorageKeyFromPath(currentPath);
      localStorage.setItem(storageKey, JSON.stringify(this.config));
    } catch (e) {
      console.warn('테마 설정을 localStorage에 저장하는 데 실패했습니다.', e);
    }
  },

  /**
   * 현재 설정을 DB에 최종 저장
   * @returns {boolean} 성공 여부
   */
  saveToDB: async function () {
    try {
      // 현재 경로 확인하여 workspaceId 결정
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
      const workspaceId = getWorkspaceIdFromPath(currentPath);
      const storageKey = getStorageKeyFromPath(currentPath);

      // get()을 통해 현재 최신 설정을 가져옴 (React Context의 값 포함)
      const configToSave = this.get();

      const payload = { themeData: JSON.stringify(configToSave) };

      const response = await fetch(`/api/system/theme-config/workspace/${workspaceId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        // DB 저장 성공 시 해당 경로의 localStorage 초기화
        localStorage.removeItem(storageKey);
        if(window.isDevConsoleEnabled()) console.log(`테마 설정이 DB에 성공적으로 저장되었습니다. (workspaceId: ${workspaceId})`);
        return true;
      } else {
        const errorText = await response.text();
        console.error(`DB에 테마 설정 저장 실패 (workspaceId: ${workspaceId}):`, response.status, errorText);
        return false;
      }
    } catch (e) {
      console.error('DB에 테마 설정 저장 중 예외 발생:', e);
      return false;
    }
  },

  get: function (key) {
    if (typeof window !== 'undefined' && window.__ThemeContextInstance) {
      const themeContext = window.__ThemeContextInstance;
      if (themeContext && themeContext.get) {
        return themeContext.get(key);
      }
    }

    if (!key) return this.config;
    return key.split('.').reduce((o, i) => (o ? o[i] : undefined), this.config);
  },

  set: function (key, value) {
    // React 컨텍스트가 있으면 우선적으로 사용
    if (typeof window !== 'undefined' && window.__ThemeContextInstance) {
      const themeContext = window.__ThemeContextInstance;
      if (themeContext && themeContext.set) {
        themeContext.set(key, value); // 컨텍스트의 set은 내부적으로 saveToLocal을 호출해야 함
        return;
      }
    }

    // 컨텍스트가 없을 경우 직접 내부 config 수정
    const keys = key.split('.');
    let target = this.config;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!target[keys[i]] || !isObject(target[keys[i]])) {
        target[keys[i]] = {};
      }
      target = target[keys[i]];
    }
    target[keys[keys.length - 1]] = value;

    this.saveToLocal(); // 변경 시 항상 localStorage에 저장
  },

  /**
   * 설정을 기본값으로 리셋 (DB 값은 보존, localStorage만 초기화)
   */
  reset: async function () {
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
    const storageKey = getStorageKeyFromPath(currentPath);

    localStorage.removeItem(storageKey);
    await this.loadThemeForPath(currentPath); // 경로에 맞는 테마 다시 로드
    if(window.isDevConsoleEnabled()) console.log('실시간 편집 내용이 초기화되었습니다. 테마 설정이 다시 로드되었습니다.');
    return this.config;
  },

  toJSON: function () {
    return JSON.stringify(this.config, null, 2);
  },

  fromJSON: function (jsonString) {
    try {
      const parsed = JSON.parse(jsonString);
      this.config = deepMerge({}, defaultConfig, parsed);
      this.saveToLocal();
      return this.config;
    } catch (e) {
      console.error("JSON 파싱 오류:", e);
      return null;
    }
  },

  log: function () {
    if(window.isDevConsoleEnabled()) console.log('현재 테마 설정:', this.config);
  },

  _deepMerge: deepMerge,
  _isObject: isObject
};

// 전역 객체로 노출
if (typeof window !== 'undefined') {
  window.ThemeConfig = ThemeConfig;

  // 페이지 로드 시 테마 초기화 실행
  // ThemeApply.js에서 이 초기화가 끝난 후 렌더링하도록 순서 조정이 필요할 수 있음
  // ThemeConfig.initialize();
}

export default ThemeConfig;
